import { motion } from "framer-motion";
import { CookingPot, Bath, Home, PaintRoller, Wrench, Lightbulb, ShieldCheck, CalendarClock, Handshake, DraftingCompass, Sofa, Sparkles } from "lucide-react";
import { IMAGES } from "@/lib/config";

const reveal = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function Services() {
  return (
    <section id="servizi" className="py-24 md:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-16">
          <span className="text-brand-terracotta font-semibold tracking-[0.2em] uppercase text-sm">I nostri servizi</span>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold text-brand-ink mt-4 mb-5">
            Ci occupiamo di tutto, dal progetto alla consegna
          </h2>
          <p className="text-brand-slate/70 text-lg">
            Un unico interlocutore per l'intera ristrutturazione: progettazione, pratiche, impianti e finiture.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 auto-rows-[220px]">
          {/* Big card: ristrutturazione completa */}
          <motion.div
            variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }}
            data-testid="service-completa"
            className="md:col-span-7 md:row-span-2 relative rounded-2xl overflow-hidden group"
          >
            <img src={IMAGES.living} alt="Ristrutturazione completa" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/90 via-brand-ink/20 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8">
              <Home className="text-brand-terracotta mb-3" size={28} />
              <h3 className="font-display text-3xl font-bold text-white mb-2">Ristrutturazione completa</h3>
              <p className="text-white/80 max-w-md">Rifacimento totale di appartamenti e case: demolizioni, impianti, massetti, pavimenti e finiture chiavi in mano.</p>
            </div>
          </motion.div>

          {/* Cucina */}
          <motion.div
            variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }}
            data-testid="service-cucina"
            className="md:col-span-5 relative rounded-2xl overflow-hidden group"
          >
            <img src={IMAGES.kitchen} alt="Cucina" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-brand-ink/45 group-hover:bg-brand-ink/55 transition-colors" />
            <div className="relative h-full flex flex-col justify-end p-7">
              <CookingPot className="text-white mb-2" size={24} />
              <h3 className="font-display text-2xl font-bold text-white">Cucine</h3>
            </div>
          </motion.div>

          {/* Bagno */}
          <motion.div
            variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }}
            data-testid="service-bagno"
            className="md:col-span-5 relative rounded-2xl overflow-hidden group"
          >
            <img src={IMAGES.bathroom} alt="Bagno" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-brand-ink/45 group-hover:bg-brand-ink/55 transition-colors" />
            <div className="relative h-full flex flex-col justify-end p-7">
              <Bath className="text-white mb-2" size={24} />
              <h3 className="font-display text-2xl font-bold text-white">Bagni</h3>
            </div>
          </motion.div>

          {/* Three small cards */}
          {[
            { icon: ShieldCheck, title: "Garanzia sui tempi", desc: "Promettiamo ciò che realmente è possibile." },
            { icon: CalendarClock, title: "Pianificazione dei lavori", desc: "Per evitare contrattempi, imprevisti e sorprese economiche." },
            { icon: DraftingCompass, title: "Progetto con architetto", desc: "Il cliente può chiedere di essere seguito da un architetto interno alla ditta." },
            { icon: Sofa, title: "Interior Design", desc: "Ogni progetto può avvalersi di un interior design se il cliente lo desidera." },
            { icon: Handshake, title: "Serietà per ogni cliente", desc: "Non iniziamo lavori se non possiamo rispettare ciò che promettiamo." },
            { icon: PaintRoller, title: "Tinteggiature & finiture", desc: "Cartongesso, controsoffitti, resine e pitture decorative." },
            { icon: Wrench, title: "Impianti", desc: "Elettrico, idraulico e climatizzazione a norma." },
            { icon: Lightbulb, title: "Progettazione & pratiche", desc: "Rilievi, render 3D e gestione pratiche edilizie." },
            { icon: Sparkles, title: "Pulizia completa", desc: "Su richiesta del cliente puliamo l'intero appartamento da cima a fondo dopo la ristrutturazione." },
          ].map((s) => (
            <motion.div
              key={s.title}
              variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }}
              data-testid={`service-${s.title}`}
              className="md:col-span-4 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-xl transition-shadow p-7 flex flex-col justify-center"
            >
              <s.icon className="text-brand-terracotta mb-3" size={26} />
              <h3 className="font-display text-xl font-bold text-brand-ink mb-1.5">{s.title}</h3>
              <p className="text-brand-slate/65 text-sm leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
